/* LOGOS — Bundle Builder (Taste)
   - Slot cards (2–6) + browse-first modal picker
   - Size selection stays on cards (no size selection in modal)
   - Shows scent NAME on cards + modal (data.products[].scentName)
   - Modal collection filter populated from product.collections (NO TAGS REQUIRED)
   - Cart add uses Dawn/Taste "sections" payload and replaces cart-drawer + cart icon bubble (NO refresh)
*/

(() => {
  const ROOT_SELECTOR = '[data-bb-root]';
  const DATA_SELECTOR = 'script[data-bb-data]';

  // ---------- helpers ----------
  const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

  const normalizeToken = (s) =>
    String(s || '')
      .replace(/["']/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const explodeCategories = (input) => {
    if (!input) return [];
    const arr = Array.isArray(input) ? input : [input];
    const out = [];
    for (const item of arr) {
      const raw = normalizeToken(item);
      if (!raw) continue;
      const split = raw
        .replace(/•/g, ',')
        .replace(/\|/g, ',')
        .split(',')
        .map(normalizeToken)
        .filter(Boolean);
      out.push(...split);
    }
    return uniq(out);
  };

  const normalizeSizeKey = (v) =>
    String(v || '').toLowerCase().replace(/\s+/g, '').trim();

  const sizeToOz = (v) => {
    const s = String(v || '').toLowerCase();
    const m = s.match(/(\d+(\.\d+)?)/);
    return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
  };

  const sortSizesNumeric = (sizes) =>
    (sizes || []).slice().sort((a, b) => {
      const ao = sizeToOz(a);
      const bo = sizeToOz(b);
      if (ao !== bo) return ao - bo;
      return String(a).localeCompare(String(b));
    });

  const dollarsToCents = (n) => Math.round(Number(n || 0) * 100);

  const money = (cents, moneyFormat) => {
    const value = (Number(cents || 0) / 100).toFixed(2);

    if (moneyFormat && String(moneyFormat).includes('{{')) {
      return String(moneyFormat).replace(/\{\{\s*amount[^}]*\}\}/, value);
    }
    return `$${value}`;
  };

  const getSectionData = (root) => {
    const dataScript = root.querySelector(DATA_SELECTOR);
    if (!dataScript) return null;
    try {
      return JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('[BB] Failed to parse JSON data.', e);
      return null;
    }
  };

  const getEls = (root) => ({
    pickers: root.querySelector('[data-bb-pickers]'),

    summary: root.querySelector('[data-bb-summary]'),
    totalRegular: root.querySelector('[data-bb-total-regular]'),
    totalSale: root.querySelector('[data-bb-total-sale]'),
    atc: root.querySelector('[data-bb-atc]'),
    notice: root.querySelector('[data-bb-notice]'),

    modal: root.querySelector('[data-bb-modal]'),
    modalClose: root.querySelectorAll('[data-bb-modal-close]'),
    modalGrid: root.querySelector('[data-bb-modal-grid]'),

    filterCollection: root.querySelector('[data-bb-filter-collection]'),
    filterScent: root.querySelector('[data-bb-filter-scent]'),
    search: root.querySelector('[data-bb-search]'),
  });

  const STORAGE_KEY = (sid) => `logos_bb_${sid}`;

  /* -----------------------------
     Cart drawer refresh (Taste/Dawn) — matches your WORKING script
  ----------------------------- */

  function replaceShopifySection(sectionName, html) {
    const wrapperId = `shopify-section-${sectionName}`;
    const existing = document.getElementById(wrapperId);
    if (!existing) return false;

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const incomingWrapper = temp.querySelector(`#${CSS.escape(wrapperId)}`);
    if (incomingWrapper) {
      existing.replaceWith(incomingWrapper);
      return true;
    }

    existing.innerHTML = html;
    return true;
  }

  async function refreshCartUIFromSections(sections) {
    const names = ['cart-icon-bubble', 'cart-drawer'];

    names.forEach((name) => {
      const html = sections?.[name];
      if (html) replaceShopifySection(name, html);
    });

    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.remove('is-empty');

    // Prefer theme API if present
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents({ sections });
    }
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }

    // These events don't hurt; some themes listen to them
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  async function addItemsToCart(items) {
    const sections = ['cart-drawer', 'cart-icon-bubble'];

    const routes = window.Shopify?.routes || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';

    const res = await fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const data = await res.json();

    if (!res.ok || data?.status) {
      const msg = data?.description || data?.message || 'Could not add to cart.';
      throw new Error(msg);
    }

    if (data.sections) {
      await refreshCartUIFromSections(data.sections);
    } else {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }

    return data;
  }

  // ---------- main ----------
  function initOne(root) {
    const data = getSectionData(root);
    if (!data) return;

    const els = getEls(root);
    if (!els.pickers) return;

    const settings = data.settings || {};
    const productsRaw = Array.isArray(data.products) ? data.products : [];

    // Normalize product data
    const products = productsRaw
      .filter((p) => p && p.id)
      .map((p) => {
        const scentCats = explodeCategories(p.scent);
        const scentName = normalizeToken(p.scentName);
        const collections = Array.isArray(p.collections) ? p.collections : [];
        return { ...p, scent: scentCats, scentName, collections };
      });

    // Filter lists
    const allCats = uniq(products.flatMap((p) => p.scent)).sort((a, b) => a.localeCompare(b));
    const allCollections = uniq(
      products.flatMap((p) => (p.collections || []).map((c) => c && c.title).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

    // State
    const state = {
      activeSlot: 0,
      slots: [],
      page: 1,
      pageSize: Number(settings.modalPageSize || 24),
      fCollection: '',
      fScent: '',
      q: '',
    };

    const maxItems = Number(settings.maxItems || 3);
    const minItems = Number(settings.minItems || 3);
    const DEFAULT_SIZE = settings.defaultSizeValue || '6oz';

    state.slots = Array.from({ length: maxItems }, () => ({
      productId: null,
      variantId: null,
      sizeValue: DEFAULT_SIZE,
      qty: 1,
    }));

    // persist
    const loadPersisted = () => {
      if (!settings.persistSelection) return;
      try {
        const raw = localStorage.getItem(STORAGE_KEY(data.sectionId));
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (saved && Array.isArray(saved.slots) && saved.slots.length === state.slots.length) {
          state.slots = saved.slots.map((s) => ({
            productId: s?.productId ?? null,
            variantId: s?.variantId ?? null,
            sizeValue: s?.sizeValue ?? DEFAULT_SIZE,
            qty: s?.qty ?? 1,
          }));
        }
      } catch (e) {}
    };

    const savePersisted = () => {
      if (!settings.persistSelection) return;
      try {
        localStorage.setItem(STORAGE_KEY(data.sectionId), JSON.stringify({ slots: state.slots }));
      } catch (e) {}
    };

    loadPersisted();

    // data helpers
    const findProduct = (id) => products.find((p) => Number(p.id) === Number(id));

    const findSizeOptionIndex = (product) => {
      const sizeName = String(settings.sizeOptionName || 'Size').toLowerCase();
      const opts = product?.options || [];
      return opts.findIndex((o) => String(o || '').toLowerCase() === sizeName);
    };

    const getAvailableSizes = (product) => {
      const idx = findSizeOptionIndex(product);
      if (idx < 0) return [];
      const sizes = uniq((product.variants || []).map((v) => (v.options || [])[idx]).filter(Boolean));
      return sortSizesNumeric(sizes);
    };

    const findVariantForSize = (product, sizeValue) => {
      const idx = findSizeOptionIndex(product);
      const list = product?.variants || [];
      if (idx < 0) return list.find((v) => v.available) || list[0] || null;

      const targetKey = normalizeSizeKey(sizeValue || '');
      const exact = list.find((v) => normalizeSizeKey((v.options || [])[idx] || '') === targetKey);
      if (exact) return exact;

      const six = list.find(
        (v) => normalizeSizeKey((v.options || [])[idx] || '') === '6oz' && v.available
      );
      if (six) return six;

      return list.find((v) => v.available) || list[0] || null;
    };

    const selectedSlots = () => state.slots.filter((s) => s && s.variantId);

    const canAdd = () => {
      const selectedCount = selectedSlots().length;
      if (settings.disableUntilMinSelected && selectedCount < minItems) return false;
      return selectedCount > 0;
    };

    const computeTotals = () => {
      const slots = selectedSlots();

      const prices = slots.map((s) => {
        const p = findProduct(s.productId);
        const v = p?.variants?.find((vv) => Number(vv.id) === Number(s.variantId));
        const price = Number(v?.price || 0);
        const qty = Number(s.qty || 1);
        return price * qty;
      });

      const regular = prices.reduce((a, b) => a + b, 0);
      const showSale = slots.length >= minItems;

      let sale = regular;
      const mode = String(settings.discountMode || 'percent');

      if (showSale) {
        if (mode === 'percent') {
          const pct = Number(settings.discountPercent ?? 33);
          const mult = Math.max(0, Math.min(1, (100 - pct) / 100));
          sale = Math.round(regular * mult);
        } else if (mode === 'fixed_total') {
          sale = Math.max(0, dollarsToCents(settings.discountFixedTotal));
        } else if (mode === 'fixed_amount') {
          sale = Math.max(0, regular - dollarsToCents(settings.discountFixedAmount));
        } else if (mode === 'cheapest_free') {
          const cheapest = prices.length ? Math.min(...prices) : 0;
          sale = Math.max(0, regular - cheapest);
        } else {
          sale = regular; // shopify_auto or unknown
        }
      }

      return { regular, sale, showSale, count: slots.length, mode };
    };

    // ---------- UI render ----------
    const renderSummary = () => {
      if (!els.summary || !els.totalRegular || !els.totalSale || !els.atc) return;

      const { regular, sale, showSale, count } = computeTotals();

      els.summary.hidden = count === 0;

      els.totalRegular.innerHTML = showSale
        ? `<span class="bb__total-regular-strike">${money(regular, data.moneyFormat)}</span>`
        : money(regular, data.moneyFormat);

      els.totalSale.textContent = money(sale, data.moneyFormat);

      els.atc.disabled = !canAdd();

      if (els.notice) {
        if (!canAdd()) {
          els.notice.textContent = settings.disabledButtonText || `Pick ${minItems} to continue.`;
        } else {
          els.notice.textContent = '';
        }
      }
    };

    const openModal = (slotIdx) => {
      state.activeSlot = slotIdx;
      state.page = 1;
      if (els.modal) els.modal.hidden = false;
      renderModalFilters();
      renderModalGrid();
    };

    const closeModal = () => {
      if (els.modal) els.modal.hidden = true;
    };

    const clearSlot = (slotIdx) => {
      state.slots[slotIdx] = { productId: null, variantId: null, sizeValue: DEFAULT_SIZE, qty: 1 };
      savePersisted();
      renderPickers();
    };

    const passesFilters = (p) => {
      if (settings.hideUnavailableProducts && p.available === false) return false;

      if (state.q) {
        const q = state.q.toLowerCase();
        const cats = Array.isArray(p.scent) ? p.scent.join(' ') : '';
        const hay = `${p.title || ''} ${p.scentName || ''} ${cats}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      if (state.fCollection) {
        const want = state.fCollection.toLowerCase();
        const titles = (p.collections || []).map((c) => String(c.title || '').toLowerCase());
        if (!titles.includes(want)) return false;
      }

      if (state.fScent) {
        const cats = (p.scent || []).map((x) => String(x).toLowerCase());
        if (!cats.includes(state.fScent.toLowerCase())) return false;
      }

      return true;
    };

    const renderModalFilters = () => {
      if (els.filterCollection) {
        els.filterCollection.innerHTML = `<option value="">All collections</option>`;
        allCollections.forEach((t) => {
          const opt = document.createElement('option');
          opt.value = t;
          opt.textContent = t;
          els.filterCollection.appendChild(opt);
        });
        els.filterCollection.value = state.fCollection || '';
      }

      if (els.filterScent) {
        els.filterScent.innerHTML = `<option value="">All scents</option>`;
        allCats.forEach((c) => {
          const opt = document.createElement('option');
          opt.value = c;
          opt.textContent = c;
          els.filterScent.appendChild(opt);
        });
        els.filterScent.value = state.fScent || '';
      }
    };

    const renderModalGrid = () => {
      if (!els.modalGrid) return;
      els.modalGrid.innerHTML = '';

      const filtered = products.filter(passesFilters);
      const end = state.pageSize * state.page;
      const visible = filtered.slice(0, end);

      visible.forEach((p) => {
        const imgSrc = p?.image?.src || data.placeholderImage || '';
        const scentName = p.scentName || '';
        const item = document.createElement('div');
        item.className = 'bb__modal-item';
        item.innerHTML = `
          <img class="bb__img" src="${imgSrc}" alt="${(p?.image?.alt || p.title || '').replace(/"/g, '&quot;')}" loading="lazy" />
          <h4>${p.title || ''}</h4>
          <p class="bb__modal-scent">${scentName}</p>
        `;

        item.addEventListener('click', () => {
          const slotIdx = state.activeSlot;
          const slot = state.slots[slotIdx];
          slot.productId = p.id;

          const sizes = getAvailableSizes(p);
          const hasSix = sizes.some((s) => normalizeSizeKey(s) === '6oz');
          if (hasSix) slot.sizeValue = sizes.find((s) => normalizeSizeKey(s) === '6oz');
          else if (!slot.sizeValue) slot.sizeValue = sizes[0] || DEFAULT_SIZE;

          const v = findVariantForSize(p, slot.sizeValue);
          if (v) {
            slot.variantId = v.id;
            const idx = findSizeOptionIndex(p);
            if (idx >= 0) slot.sizeValue = (v.options || [])[idx] || slot.sizeValue;
          } else {
            slot.variantId = null;
          }

          savePersisted();
          renderPickers();
          closeModal();
        });

        els.modalGrid.appendChild(item);
      });
    };

    const renderPickers = () => {
      els.pickers.innerHTML = '';

      for (let i = 0; i < maxItems; i++) {
        const slot = state.slots[i];
        const p = slot.productId ? findProduct(slot.productId) : null;

        const imgSrc = p?.image?.src || data.placeholderImage || '';
        const title = p ? p.title || '' : `Candle ${i + 1}`;
        const scentName = p ? p.scentName || '' : '';
        const sizes = p ? getAvailableSizes(p) : [];

        let variant = null;
        if (p) {
          if (!slot.sizeValue) slot.sizeValue = DEFAULT_SIZE;

          const hasSix = sizes.some((s) => normalizeSizeKey(s) === '6oz');
          if (hasSix && normalizeSizeKey(slot.sizeValue) !== '6oz') {
            slot.sizeValue = sizes.find((s) => normalizeSizeKey(s) === '6oz');
          }

          variant =
            p.variants?.find((vv) => Number(vv.id) === Number(slot.variantId)) ||
            findVariantForSize(p, slot.sizeValue);

          if (variant) {
            slot.variantId = variant.id;
            const idx = findSizeOptionIndex(p);
            if (idx >= 0) slot.sizeValue = (variant.options || [])[idx] || slot.sizeValue;
          }
        }

        const card = document.createElement('div');
        card.className = 'bb__slotcard';
        card.innerHTML = `
          <img class="bb__img" src="${imgSrc}" alt="${(p?.image?.alt || title || '').replace(/"/g, '&quot;')}" loading="lazy" />
          <h3 class="bb__product-title">${title}</h3>
          <p class="bb__scent">${scentName}</p>
          <p class="bb__price">${p && variant ? money(variant.price, data.moneyFormat) : ''}</p>
          <div class="bb__pills" ${sizes.length ? '' : 'hidden'}></div>
          <div class="bb__actions">
  <button type="button" class="bb__btn bb__btn--change" data-action="choose" data-slot="${i}">
    ${p ? 'Change' : 'Choose'}
  </button>

  <button type="button" class="bb__btn bb__btn--remove" data-action="remove" data-slot="${i}" ${p ? '' : 'hidden'}>
    Remove
  </button>
</div>

        `;

        // size pills
        const pillsWrap = card.querySelector('.bb__pills');
        if (p && sizes.length && pillsWrap) {
          sizes.forEach((size) => {
            const pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'bb__pill';

            const vv = findVariantForSize(p, size);
            const disabled = !vv || !vv.available;

            pill.textContent = size;
            pill.setAttribute(
              'aria-pressed',
              String(normalizeSizeKey(slot.sizeValue) === normalizeSizeKey(size))
            );

            if (disabled) pill.classList.add('is-disabled');

            pill.addEventListener('click', () => {
              if (disabled) return;
              slot.sizeValue = size;
              const vvv = findVariantForSize(p, size);
              if (vvv) slot.variantId = vvv.id;
              savePersisted();
              renderPickers();
            });

            pillsWrap.appendChild(pill);
          });
        }

        // actions
        card.querySelectorAll('[data-action]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const action = btn.getAttribute('data-action');
            const slotIdx = Number(btn.getAttribute('data-slot'));
            if (action === 'choose') openModal(slotIdx);
            if (action === 'remove') clearSlot(slotIdx);
          });
        });

        els.pickers.appendChild(card);
      }

      renderSummary();
    };

    // ---------- events ----------
    if (els.modalClose) els.modalClose.forEach((el) => el.addEventListener('click', closeModal));

    if (els.search) {
      els.search.addEventListener('input', (e) => {
        state.q = e.target.value || '';
        state.page = 1;
        renderModalGrid();
      });
    }

    if (els.filterScent) {
      els.filterScent.addEventListener('change', (e) => {
        state.fScent = e.target.value || '';
        state.page = 1;
        renderModalGrid();
      });
    }

    if (els.filterCollection) {
      els.filterCollection.addEventListener('change', (e) => {
        state.fCollection = e.target.value || '';
        state.page = 1;
        renderModalGrid();
      });
    }

    // Add to cart — using the WORKING cart drawer refresh approach
    if (els.atc) {
      els.atc.addEventListener('click', async () => {
        try {
          if (!canAdd()) return;

          els.atc.disabled = true;
          if (els.notice) els.notice.textContent = 'Adding…';

          const items = selectedSlots().map((s) => ({
            id: Number(s.variantId),
            quantity: Number(s.qty || 1),
            properties: { _bundle: 'Bundle Builder' },
          }));

          await addItemsToCart(items);

          if (els.notice) els.notice.textContent = settings.addedMessage || 'Added to cart';
        } catch (e) {
          console.error(e);
          if (els.notice) els.notice.textContent = e.message || 'Could not add to cart.';
        } finally {
          renderSummary();
        }
      });
    }

    // ---------- first render ----------
    renderPickers();
    renderSummary();
  }

  // ---------- initAll (editor-safe) ----------
  const initAll = (scope = document) => {
    const roots = [];

    if (scope && scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope && scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      // prevent double-init if Shopify re-renders the same DOM
      const already = root.dataset.bbInited === '1';
      const dataScript = root.querySelector(DATA_SELECTOR);
      const uid = dataScript ? (dataScript.dataset.bbUid || '') : '';

      if (!dataScript) return;

      if (!dataScript.dataset.bbUid) {
        dataScript.dataset.bbUid = String(Date.now()) + Math.random().toString(16).slice(2);
      }

      if (already && root.dataset.bbUid === dataScript.dataset.bbUid) return;

      root.dataset.bbInited = '1';
      root.dataset.bbUid = dataScript.dataset.bbUid;

      initOne(root);
    });
  };

  // First paint
  initAll();

  // Theme editor hooks
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:reorder', (e) => initAll(e.target));
  document.addEventListener('shopify:section:select', (e) => initAll(e.target));
  document.addEventListener('shopify:section:render', (e) => initAll(e.target));
  document.addEventListener('shopify:block:select', (e) => initAll(e.target));
  document.addEventListener('shopify:block:deselect', (e) => initAll(e.target));

  window.addEventListener('pageshow', () => initAll());
})();
